import React from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Skeleton,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { FaCreditCard, FaCrown, FaRocket } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useBillingStatus, useCheckout, useBillingPortal } from '../hooks/useBilling';
import { getEffectivePlan, PLAN_PRICING, PlanType } from '../utils/planConfig';
import { formatDateDMY } from '../utils/formatDate';
import UsageMetrics from './plan/UsageMetrics';

const PlanBillingSection: React.FC = () => {
  const { organization } = useAuth();
  const { data: billing, isLoading } = useBillingStatus();
  const checkout = useCheckout();
  const portal = useBillingPortal();
  const toast = useToast();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const insetBg = useColorModeValue('gray.50', 'gray.700');
  const proCardBg = useColorModeValue('purple.50', 'purple.900');
  const heroBg = useColorModeValue(
    'linear(to-r, blue.500, purple.600)',
    'linear(to-r, blue.600, purple.700)'
  );

  const effectivePlan = getEffectivePlan(
    organization?.plan || 'free',
    organization?.trialEndsAt
  );
  const trialActive =
    !!organization?.trialEndsAt && new Date(organization.trialEndsAt) > new Date();

  const handleUpgrade = (plan: 'starter' | 'pro') => {
    checkout.mutate(
      { plan, seats: billing?.activeUsers || 1 },
      {
        onError: (err: any) => {
          toast({
            title: err.response?.data?.message || 'Checkout failed',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
        },
      }
    );
  };

  const handleManageBilling = () => {
    portal.mutate(undefined, {
      onError: (err: any) => {
        toast({
          title: err.response?.data?.message || 'Could not open billing portal',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      },
    });
  };

  const planBadgeColor = (plan: PlanType) => {
    switch (plan) {
      case 'pro':
        return 'purple';
      case 'starter':
        return 'blue';
      case 'enterprise':
        return 'orange';
      default:
        return 'gray';
    }
  };

  if (isLoading) {
    return (
      <Box bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" p={6}>
        <Skeleton height="28px" width="200px" mb={4} />
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Skeleton height="80px" borderRadius="lg" />
          <Skeleton height="80px" borderRadius="lg" />
          <Skeleton height="80px" borderRadius="lg" />
        </SimpleGrid>
      </Box>
    );
  }

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      boxShadow="sm"
    >
      <Flex
        bgGradient={heroBg}
        px={{ base: 5, md: 6 }}
        py={5}
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={3}
      >
        <Box>
          <Heading size="md" color="white">
            Plan & billing
          </Heading>
          <Text color="whiteAlpha.900" fontSize="sm" mt={1}>
            {organization?.name || 'Your workspace'}
          </Text>
        </Box>
        <Badge
          colorScheme={planBadgeColor(effectivePlan)}
          fontSize="sm"
          px={3}
          py={1}
          borderRadius="full"
          textTransform="none"
        >
          {PLAN_PRICING[effectivePlan].label}
          {trialActive && ' · Trial'}
        </Badge>
      </Flex>

      <Box p={{ base: 5, md: 6 }}>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={6}>
          <Box bg={insetBg} borderRadius="lg" p={4}>
            <Text fontSize="xs" fontWeight="semibold" color={mutedText} textTransform="uppercase">
              Active users
            </Text>
            <Text fontSize="2xl" fontWeight="bold" mt={1}>
              {billing?.activeUsers ?? 0}
            </Text>
            <Text fontSize="xs" color={mutedText} mt={1}>
              {billing?.seatCount ? `${billing.seatCount} seats billed` : 'Free tier'}
            </Text>
          </Box>
          <Box bg={insetBg} borderRadius="lg" p={4}>
            <Text fontSize="xs" fontWeight="semibold" color={mutedText} textTransform="uppercase">
              Plan price
            </Text>
            <Text fontSize="2xl" fontWeight="bold" mt={1}>
              {PLAN_PRICING[effectivePlan].price != null
                ? `$${PLAN_PRICING[effectivePlan].price}`
                : 'Custom'}
            </Text>
            <Text fontSize="xs" color={mutedText} mt={1}>
              per user / month
            </Text>
          </Box>
          {trialActive && (
            <Box bg={insetBg} borderRadius="lg" p={4}>
              <Text fontSize="xs" fontWeight="semibold" color={mutedText} textTransform="uppercase">
                Trial ends
              </Text>
              <Text fontSize="2xl" fontWeight="bold" mt={1}>
                {formatDateDMY(organization!.trialEndsAt!)}
              </Text>
              <Text fontSize="xs" color={mutedText} mt={1}>
                Pro features during trial
              </Text>
            </Box>
          )}
          {organization?.plan !== 'free' && (
            <Flex
              bg={insetBg}
              borderRadius="lg"
              p={4}
              align="center"
              justify="center"
              gridColumn={{ base: '1 / -1', lg: trialActive ? 'auto' : 'span 2' }}
            >
              <Button
                leftIcon={<Icon as={FaCreditCard} />}
                variant="outline"
                size="sm"
                onClick={handleManageBilling}
                isLoading={portal.isPending}
              >
                Manage billing & invoices
              </Button>
            </Flex>
          )}
        </SimpleGrid>

        <Divider mb={6} />

        <Heading size="sm" mb={4}>
          Workspace usage
        </Heading>
        <UsageMetrics />

        {effectivePlan === 'free' && (
          <>
            <Divider my={6} />
            <Heading size="sm" mb={4}>
              Upgrade your workspace
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                p={5}
                transition="all 0.2s"
                _hover={{ borderColor: 'blue.300', boxShadow: 'md' }}
              >
                <HStack mb={3}>
                  <Flex
                    align="center"
                    justify="center"
                    w={9}
                    h={9}
                    borderRadius="lg"
                    bg="blue.50"
                    color="blue.500"
                  >
                    <Icon as={FaRocket} />
                  </Flex>
                  <Box>
                    <Heading size="sm">Starter</Heading>
                    <Text fontSize="sm" color={mutedText}>
                      ${PLAN_PRICING.starter.price}/user/mo
                    </Text>
                  </Box>
                </HStack>
                <Text fontSize="sm" color={mutedText} mb={4}>
                  Unlimited projects, clients, and file uploads for growing teams.
                </Text>
                <Button
                  colorScheme="blue"
                  size="sm"
                  w="full"
                  isLoading={checkout.isPending}
                  onClick={() => handleUpgrade('starter')}
                >
                  Upgrade to Starter
                </Button>
              </Box>

              <Box
                border="2px solid"
                borderColor="purple.300"
                borderRadius="xl"
                p={5}
                bg={proCardBg}
                transition="all 0.2s"
                _hover={{ boxShadow: 'md' }}
              >
                <HStack mb={3}>
                  <Flex
                    align="center"
                    justify="center"
                    w={9}
                    h={9}
                    borderRadius="lg"
                    bg="purple.100"
                    color="purple.600"
                  >
                    <Icon as={FaCrown} />
                  </Flex>
                  <Box>
                    <Heading size="sm">Pro</Heading>
                    <Text fontSize="sm" color={mutedText}>
                      ${PLAN_PRICING.pro.price}/user/mo
                    </Text>
                  </Box>
                  <Badge colorScheme="purple" ml="auto">
                    Popular
                  </Badge>
                </HStack>
                <Text fontSize="sm" color={mutedText} mb={4}>
                  Analytics, role management, and quoted-value reporting.
                </Text>
                <Button
                  colorScheme="purple"
                  size="sm"
                  w="full"
                  isLoading={checkout.isPending}
                  onClick={() => handleUpgrade('pro')}
                >
                  Upgrade to Pro
                </Button>
              </Box>
            </SimpleGrid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default PlanBillingSection;
